import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { WEEKDAYS } from "../../lib/listingConstants";

/**
 * Phase 3.55 — Sub-Komponente aus CreateListingPage extrahiert, um die Datei
 * lesbar zu halten. Reines Präsentations-Element ohne eigenen State.
 *
 * Props:
 *   form   — Form-State aus CreateListingPage
 *   update — (key, value) Setter
 *   visible — boolean: nur rendern, wenn `user.program_sharing` true ist.
 */
export default function SharingFieldsSection({ form, update, visible, hideHeader = false }) {
    if (!visible) return null;
    return (
        <div className="bg-white border border-gray-200 rounded-sm p-6" data-testid="sharing-toggle-section">
            {!hideHeader && (
                <label className="flex items-start gap-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={!!form.sharing_enabled}
                        onChange={(e) => update("sharing_enabled", e.target.checked)}
                        className="mt-1 w-5 h-5 accent-[#10B981]"
                        data-testid="sharing-toggle-checkbox"
                    />
                    <div>
                        <div className="font-bold text-[#111827]">Sharing / Vermietung anbieten</div>
                        <div className="text-xs text-gray-600 mt-0.5">
                            Interessenten können dieses Fahrzeug auch tageweise mieten oder im Sharing-Modell nutzen.
                        </div>
                    </div>
                </label>
            )}

            {(form.sharing_enabled || hideHeader) && (
                <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${hideHeader ? "" : "mt-4 pt-4 border-t border-gray-100"}`} data-testid="sharing-fields">
                    <div>
                        <Label className="text-xs font-bold uppercase text-gray-500">Preis pro Tag (€)</Label>
                        <Input type="number" min="0" step="1" value={form.sharing_price_per_day} onChange={(e) => update("sharing_price_per_day", e.target.value)} className="mt-1.5 rounded-sm font-mono-num" data-testid="sharing-price-per-day" />
                    </div>
                    <div>
                        <Label className="text-xs font-bold uppercase text-gray-500">Kaution (€)</Label>
                        <Input type="number" min="0" step="1" value={form.sharing_deposit_eur} onChange={(e) => update("sharing_deposit_eur", e.target.value)} className="mt-1.5 rounded-sm font-mono-num" data-testid="sharing-deposit" />
                    </div>
                    {/* Jun-2026 (User-Wunsch) — Tarife Woche / Monat / Saison.
                        Pro Periode ein Preis; die Saison hat zusätzlich einen Von/Bis-Zeitraum. */}
                    <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 mt-2 border-t border-gray-100" data-testid="sharing-period-rates">
                        <div>
                            <Label className="text-xs font-bold uppercase text-gray-500">Preis pro Woche (€)</Label>
                            <Input type="number" min="0" step="1" value={form.sharing_price_per_week} onChange={(e) => update("sharing_price_per_week", e.target.value)} placeholder="z. B. 250" className="mt-1.5 rounded-sm font-mono-num" data-testid="sharing-price-per-week" />
                        </div>
                        <div>
                            <Label className="text-xs font-bold uppercase text-gray-500">Preis pro Monat (€)</Label>
                            <Input type="number" min="0" step="1" value={form.sharing_price_per_month} onChange={(e) => update("sharing_price_per_month", e.target.value)} placeholder="z. B. 800" className="mt-1.5 rounded-sm font-mono-num" data-testid="sharing-price-per-month" />
                        </div>
                        <div>
                            <Label className="text-xs font-bold uppercase text-gray-500">Preis pro Saison (€)</Label>
                            <Input type="number" min="0" step="1" value={form.sharing_price_per_season} onChange={(e) => update("sharing_price_per_season", e.target.value)} placeholder="z. B. 2.500" className="mt-1.5 rounded-sm font-mono-num" data-testid="sharing-price-per-season" />
                        </div>
                    </div>
                    <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="sharing-season-range">
                        <div>
                            <Label className="text-xs font-bold uppercase text-gray-500">Saison von</Label>
                            <Input type="date" value={form.sharing_season_from} onChange={(e) => update("sharing_season_from", e.target.value)} className="mt-1.5 rounded-sm" data-testid="sharing-season-from" />
                        </div>
                        <div>
                            <Label className="text-xs font-bold uppercase text-gray-500">Saison bis</Label>
                            <Input type="date" value={form.sharing_season_to} onChange={(e) => update("sharing_season_to", e.target.value)} min={form.sharing_season_from || undefined} className="mt-1.5 rounded-sm" data-testid="sharing-season-to" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-bold uppercase text-gray-500">Mindest-Mietdauer (Tage)</Label>
                        <Input type="number" min="1" value={form.sharing_min_days} onChange={(e) => update("sharing_min_days", e.target.value)} className="mt-1.5 rounded-sm" data-testid="sharing-min-days" />
                    </div>
                    <div>
                        <Label className="text-xs font-bold uppercase text-gray-500">Maximal-Mietdauer (Tage)</Label>
                        <Input type="number" min="1" value={form.sharing_max_days} onChange={(e) => update("sharing_max_days", e.target.value)} className="mt-1.5 rounded-sm" data-testid="sharing-max-days" />
                    </div>
                    <div className="md:col-span-2">
                        <Label className="text-xs font-bold uppercase text-gray-500">Abhol-Adresse (Default: Händler-Adresse)</Label>
                        <Input value={form.sharing_pickup_address} onChange={(e) => update("sharing_pickup_address", e.target.value)} placeholder="Straße, PLZ Ort" className="mt-1.5 rounded-sm" data-testid="sharing-pickup-address" />
                    </div>
                    <div className="md:col-span-2">
                        <Label className="text-xs font-bold uppercase text-gray-500">Verfügbare Wochentage</Label>
                        <div className="mt-1.5 flex flex-wrap gap-1" data-testid="sharing-weekdays">
                            {WEEKDAYS.map((d) => {
                                const active = (form.sharing_available_weekdays || []).includes(d);
                                return (
                                    <button
                                        key={d}
                                        type="button"
                                        onClick={() => {
                                            const cur = form.sharing_available_weekdays || [];
                                            update("sharing_available_weekdays", active ? cur.filter((x) => x !== d) : [...cur, d]);
                                        }}
                                        className={`px-3 py-1.5 text-xs font-bold rounded-sm border transition-colors ${active ? "bg-emerald-600 border-emerald-700 text-white" : "bg-white border-gray-300 text-gray-700 hover:border-emerald-500"}`}
                                        data-testid={`sharing-weekday-${d}`}
                                    >
                                        {d}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
